import type { AuthUrlParams } from './core';
import type { ICommonParams, IUserInfoParams } from '@/utils/storage';

/** 日志前缀 */
export const LOG_PREFIX = '[WxAuth]';

/** 需要从 URL 中清除的授权参数 */
export const AUTH_URL_PARAMS = [
  // 'token',
  'commonParams',
  'userInfo',
  'code',
  'state',
  'entryDomain',
  'authDomain',
  'liveDomain',
  // 注意：openId 和 unionId 是业务参数，在跨域名跳转时需要保留
  // 特别是小程序场景，每次进入都会携带这些参数，不应该被过滤
  // 'unionId',
  // 'openId',
  'needWxAuth',
] as const;

/** 日志开关（localStorage） */
const LOG_SWITCH_KEY = 'BELINK-WXAUTH-LOG';

/** 是否开启 wxAuth 日志 */
function isLogEnabled(): boolean {
  if (import.meta.env.VITE_WXAUTH_LOG === 'true') return true;
  try {
    return localStorage.getItem(LOG_SWITCH_KEY) === '1';
  } catch {
    return false;
  }
}

/** 统一日志输出（info） */
export function logInfo(message: string, ...args: unknown[]): void {
  if (!isLogEnabled()) return;
  console.log(message, ...args);
}

/** 统一日志输出（error） */
export function logError(message: string, ...args: unknown[]): void {
  if (!isLogEnabled()) return;
  console.error(message, ...args);
}

/** 获取业务参数（排除授权相关参数） */
function getBusinessParams(searchParams: URLSearchParams): Record<string, string> {
  const businessParams: Record<string, string> = {};
  const excludeParams = new Set<string>(AUTH_URL_PARAMS);

  searchParams.forEach((value, key) => {
    if (!excludeParams.has(key)) {
      businessParams[key] = value;
    }
  });

  return businessParams;
}

/** 从 URL 解析授权参数 */
export function parseAuthUrlParams(searchParams: URLSearchParams): AuthUrlParams {
  const token = searchParams.get('token') || '';
  const commonParamsStr = searchParams.get('commonParams') || '';
  const userInfoStr = searchParams.get('userInfo') || '';

  let commonParams: ICommonParams | null = null;
  let userInfo: Omit<IUserInfoParams, 'token'> | null = null;

  try {
    if (commonParamsStr) {
      commonParams = JSON.parse(commonParamsStr);
    }
    if (userInfoStr) {
      userInfo = JSON.parse(userInfoStr);
    }
  } catch (error) {
    logError(`${LOG_PREFIX} 解析 URL 授权参数失败:`, error);
  }

  return { token, commonParams, userInfo };
}

/** 清除 URL 中的授权参数 */
export function clearAuthUrlParams(): void {
  const url = new URL(window.location.href);
  AUTH_URL_PARAMS.forEach((param) => {
    url.searchParams.delete(param);
  });
  window.history.replaceState({}, '', url.toString());
}

/** 生成带业务参数的 URL */
function buildUrlWithParams(baseUrl: string, params: Record<string, string>): string {
  const normalizedBaseUrl =
    baseUrl.startsWith('http://') || baseUrl.startsWith('https://')
      ? baseUrl
      : `${window.location.protocol}//${baseUrl}`;
  const url = new URL(normalizedBaseUrl);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

/** 携带业务参数重定向到目标域名 */
export function redirectToDomain(
  targetDomain: string,
  searchParams: URLSearchParams,
  extraParams?: Record<string, string>,
): void {
  const businessParams = getBusinessParams(searchParams);
  const targetUrl = buildUrlWithParams(`${targetDomain}${window.location.pathname}`, {
    ...businessParams,
    ...(extraParams || {}),
  });
  logInfo(`${LOG_PREFIX} 重定向到: ${targetUrl}`);
  window.location.replace(targetUrl);
}

/** 携带完整授权参数重定向到 C 域名（兼容旧流程） */
export function redirectToLiveWithAuth(
  liveDomain: string,
  searchParams: URLSearchParams,
  token: string,
  commonParams: ICommonParams,
  userInfo: Omit<IUserInfoParams, 'token'>,
): void {
  const businessParams = getBusinessParams(searchParams);
  const targetUrl = buildUrlWithParams(`${liveDomain}${window.location.pathname}`, {
    ...businessParams,
    token,
    commonParams: JSON.stringify(commonParams),
    userInfo: JSON.stringify(userInfo),
  });
  logInfo(`${LOG_PREFIX} 授权成功，重定向到 C 域名`);
  window.location.replace(targetUrl);
}

/** 携带授权 code 重定向到 C 域名 */
export function redirectToLiveWithCode(
  liveDomain: string,
  searchParams: URLSearchParams,
  code: string,
  extraParams?: Record<string, string>,
): void {
  const businessParams = getBusinessParams(searchParams);
  const targetUrl = buildUrlWithParams(`${liveDomain}${window.location.pathname}`, {
    ...businessParams,
    ...(extraParams || {}),
    code,
  });
  logInfo(`${LOG_PREFIX} 授权成功，重定向到 C 域名（code）`);
  window.location.replace(targetUrl);
}

/** 生成随机字符串 */
export function generateNonceStr(length = 16): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
